import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { getCenterOffers, deleteOffer, publishOffer } from '../../services/offers'
import { getUser, logout } from '../../services/helpers'
import {
  ChartBarIcon,
  CalendarIcon,
  PlusCircleIcon,
  TagIcon,
  ShoppingBagIcon,
  TicketIcon,
  WalletIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  BuildingOfficeIcon,
  Bars3Icon,
  XMarkIcon,
  ExclamationTriangleIcon,
  PhotoIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline'

const CenterOffersPage = () => {
  const navigate = useNavigate()
  const [user, setUser] = useState(getUser())
  const displayName = user?.organization || user?.name || 'Event Center'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [offers, setOffers] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('all') // all, DRAFT, ACTIVE, INACTIVE, EXPIRED
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState('info')

  const tabs = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon, path: '/center' },
    { id: 'events', label: 'My Events', icon: CalendarIcon, path: '/center/events', disabled: true },
    { id: 'offers', label: 'My Offers', icon: TagIcon, path: '/center/offers' },
    { id: 'orders', label: 'Orders', icon: ShoppingBagIcon, path: '/center/orders' },
    { id: 'tickets', label: 'Tickets', icon: TicketIcon, path: '/center/tickets' },
    { id: 'media', label: 'Media Gallery', icon: PhotoIcon, path: '/center/media', disabled: true },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/center/disputes' },
    { id: 'wallet', label: 'Wallet', icon: WalletIcon, path: '/center/wallet' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/center' }
  ]

  useEffect(() => {
    fetchOffers()
  }, [filter])

  const fetchOffers = async () => {
    setLoading(true)
    const params = filter !== 'all' ? { status: filter } : {}
    const res = await getCenterOffers(params)
    if (res.status === 200) {
      setOffers(res.data || [])
    }
    setLoading(false)
  }

  const handleDelete = async (offerId, offer) => {
    // Check if any tickets have been sold
    if (offer.remaining < offer.quantity) {
      setMessage('Cannot delete offer with sold tickets. Please archive it instead.')
      setMessageType('error')
      setTimeout(() => setMessage(''), 5000)
      return
    }

    if (!window.confirm('Are you sure you want to delete this offer?')) return

    const res = await deleteOffer(offerId)
    if (res.status === 200) {
      setMessage('Offer deleted successfully')
      setMessageType('success')
      fetchOffers()
    } else {
      setMessage(res.error || 'Failed to delete offer')
      setMessageType('error')
    }
    setTimeout(() => setMessage(''), 5000)
  }

  const handlePublish = async (offerId) => {
    const res = await publishOffer(offerId)
    if (res.status === 200) {
      setMessage('Offer published successfully')
      setMessageType('success')
      fetchOffers()
    } else {
      setMessage(res.error || 'Failed to publish offer')
      setMessageType('error')
    }
    setTimeout(() => setMessage(''), 5000)
  }

  const getStatusBadge = (status) => {
    const badges = {
      DRAFT: 'bg-gray-100 text-gray-700',
      ACTIVE: 'bg-green-100 text-green-700',
      INACTIVE: 'bg-yellow-100 text-yellow-700',
      EXPIRED: 'bg-red-100 text-red-700'
    }
    return badges[status] || 'bg-gray-100 text-gray-700'
  }

  const filteredOffers = offers

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm">
              <BuildingOfficeIcon className="w-5 h-5" />
            </div>
            <span className="text-xl font-bold text-slate-900 tracking-tight">Obule Panel</span>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.filter(tab => !tab.disabled).map((tab) => {
              const Icon = tab.icon
              const isActive = tab.id === 'offers'
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/center/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-between px-4 h-16 sticky top-0 z-40">
          <button onClick={() => setMobileMenuOpen(true)}>
            <Bars3Icon className="w-6 h-6 text-slate-600" />
          </button>
          <span className="font-semibold text-slate-900">My Offers</span>
          <div className="w-6" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">My Offers</h1>
              <p className="text-slate-600 mt-1">Create and manage your special offers</p>
            </div>
            <Link
              to="/center/offers/create"
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors shadow-lg"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create Offer
            </Link>
          </div>

          {/* Message */}
          {message && (
            <div className={`mb-6 rounded-lg border px-4 py-3 text-sm ${
              messageType === 'error' 
                ? 'border-red-200 bg-red-50 text-red-800' 
                : messageType === 'success'
                ? 'border-green-200 bg-green-50 text-green-800'
                : 'border-emerald-100 bg-emerald-50 text-emerald-800'
            }`}>
              {message}
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-wrap gap-2 mb-6">
            {['all', 'DRAFT', 'ACTIVE', 'INACTIVE', 'EXPIRED'].map(status => (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === status
                    ? 'bg-emerald-600 text-white'
                    : 'bg-white text-slate-700 border border-slate-200 hover:bg-slate-50'
                }`}
              >
                {status === 'all' ? 'All Offers' : status}
              </button>
            ))}
          </div>

          {/* Offers List */}
          {loading ? (
            <div className="text-center py-20">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
              <p className="mt-3 text-sm text-slate-500">Loading offers...</p>
            </div>
          ) : filteredOffers.length === 0 ? (
            <div className="text-center py-20">
              <svg className="mx-auto h-16 w-16 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
              </svg>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">No offers found</h3>
              <p className="mt-2 text-sm text-slate-600">Create your first offer to get started</p>
              <Link
                to="/center/offers/create"
                className="mt-6 inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-6 py-3 rounded-lg transition-colors"
              >
                Create Offer
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredOffers.map(offer => {
                const discount = Math.round(((offer.normalPrice - offer.discountedPrice) / offer.normalPrice) * 100)
                const soldCount = offer.quantity - offer.remaining
                const soldPercentage = (soldCount / offer.quantity) * 100

                return (
                  <div key={offer._id} className="bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-lg transition-shadow overflow-hidden">
                    {/* Image */}
                    <div className="relative h-48 bg-gradient-to-br from-slate-100 to-slate-50">
                      {offer.images && offer.images.length > 0 ? (
                        <img
                          src={offer.images[0]}
                          alt={offer.title}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-slate-300">
                          <svg className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                          </svg>
                        </div>
                      )}
                      {/* Status Badge */}
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusBadge(offer.status)}`}>
                          {offer.status}
                        </span>
                      </div>
                      {/* Discount Badge */}
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 bg-red-500 text-white rounded-full text-xs font-bold">
                          {discount}% OFF
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5">
                      <h3 className="text-lg font-bold text-slate-900 line-clamp-2 mb-2">{offer.title}</h3>
                      <p className="text-sm text-slate-600 line-clamp-2 mb-4">{offer.description}</p>

                      {/* Pricing */}
                      <div className="flex items-center gap-3 mb-4">
                        <span className="text-sm text-slate-500 line-through">
                          {offer.currency} {offer.normalPrice.toLocaleString()}
                        </span>
                        <span className="text-xl font-bold text-emerald-600">
                          {offer.currency} {offer.discountedPrice.toLocaleString()}
                        </span>
                      </div>

                      {/* Availability */}
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-slate-600 mb-1">
                          <span>Sold: {soldCount} / {offer.quantity}</span>
                          <span>{offer.remaining} left</span>
                        </div>
                        <div className="w-full bg-slate-100 rounded-full h-2">
                          <div
                            className="bg-emerald-600 h-2 rounded-full transition-all"
                            style={{ width: `${soldPercentage}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <Link
                          to={`/center/offers/${offer._id}`}
                          className="flex-1 text-center px-4 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-colors shadow-sm hover:shadow whitespace-nowrap"
                        >
                          View Details
                        </Link>
                        <div className="flex gap-2">
                          <Link
                            to={`/center/offers/${offer._id}/edit`}
                            className="px-3 py-2.5 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors"
                            title="Edit offer"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </Link>
                          {offer.status === 'DRAFT' && (
                            <button
                              onClick={() => handlePublish(offer._id)}
                              className="px-3 py-2.5 bg-green-50 border border-green-200 hover:bg-green-100 text-green-700 text-sm font-medium rounded-lg transition-colors"
                              title="Publish offer"
                            >
                              <CheckCircleIcon className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(offer._id, offer)}
                            className="px-3 py-2.5 bg-red-50 border border-red-200 hover:bg-red-100 text-red-700 text-sm font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            disabled={offer.remaining < offer.quantity}
                            title={offer.remaining < offer.quantity ? 'Cannot delete offer with sold tickets' : 'Delete offer'}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default CenterOffersPage

